import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AlertTriangle,
    Archive,
    Check,
    ChevronDown,
    ChevronUp,
    Coins,
    Gift,
    PackageOpen,
    PlusCircle,
    Save,
    ShieldCheck,
    Trash2,
    Upload,
    Wallet,
    X,
} from 'lucide-react';

import { API_BASE_URL } from '../../config';

const REWARD_TYPES = [
    { value: 'bonus', label: 'Бонусы', hint: 'зачисляются автоматически' },
    { value: 'telegram_gift', label: 'Telegram-подарок', hint: 'автовыдача через Telegram или Collectible Marketplace' },
    { value: 'stars', label: 'Telegram Stars', hint: 'ручная выдача' },
    { value: 'premium', label: 'Telegram Premium', hint: 'ручная выдача' },
    { value: 'digital_good', label: 'Цифровой товар WATA', hint: 'добавляется только галочкой из пула' },
    { value: 'manual', label: 'Другой приз', hint: 'ручная выдача' },
    { value: 'none', label: 'Без приза', hint: 'завершается автоматически' },
];

const DEFAULT_POLICY = {
    version: '1',
    valuable_prizes_enabled: true,
    no_prize_enabled: true,
    budget_guard_enabled: false,
    monthly_budget_rub: 0,
    bonus_point_cost_rub: 0,
    loyalty_boost_enabled: true,
    loyalty_bands: [
        { code: 'base', title: 'Базовый', min_spend_rub: 0, multiplier_percent: 100 },
        { code: 'regular', title: 'Постоянный', min_spend_rub: 5000, multiplier_percent: 110 },
        { code: 'priority', title: 'Приоритетный', min_spend_rub: 20000, multiplier_percent: 125 },
        { code: 'vip', title: 'VIP', min_spend_rub: 50000, multiplier_percent: 150 },
    ],
};

const PRIZE_VISUAL_GROUPS = [
    {
        id: 'digital',
        label: 'Цифровые товары',
        hint: 'Подходят для WATA, подписок и игровых сертификатов',
        items: [
            { value: 'digital-gaming', label: 'Игровые платформы' },
            { value: 'digital-mobile-games', label: 'Мобильные игры' },
            { value: 'digital-apps', label: 'Магазины приложений' },
            { value: 'digital-media', label: 'Подписки и медиа' },
            { value: 'digital-telegram', label: 'Telegram' },
            { value: 'digital-voucher', label: 'Сертификат' },
        ],
    },
    {
        id: 'collectible',
        label: 'Коллекционные подарки',
        hint: 'Универсальные праздничные иллюстрации',
        items: [
            { value: 'heart', label: 'Сердце' },
            { value: 'teddy', label: 'Мишка' },
            { value: 'present', label: 'Подарок' },
            { value: 'rose', label: 'Роза' },
            { value: 'cake', label: 'Торт' },
            { value: 'bouquet', label: 'Букет' },
            { value: 'rocket', label: 'Ракета' },
            { value: 'trophy', label: 'Кубок' },
            { value: 'ring', label: 'Кольцо' },
            { value: 'diamond', label: 'Бриллиант' },
            { value: 'champagne', label: 'Шампанское' },
        ],
    },
];

const PRIZE_VISUALS = PRIZE_VISUAL_GROUPS.flatMap((group) => group.items);

const inputClass = 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-400';

const draftKey = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const asInteger = (value, fallback = 0) => {
    const parsed = Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const isPersisted = (item) => Number.isInteger(item.id) && item.id > 0;

const updatePrizeDetails = (details, key, value) => {
    const next = details && typeof details === 'object' ? { ...details } : {};
    if (value === '' || value === null || value === undefined) delete next[key];
    else next[key] = value;
    return Object.keys(next).length ? next : null;
};

const GIFT_DETAIL_KEYS = [
    'gift_provider',
    'telegram_gift_id',
    'telegram_max_price_stars',
    'collectible_collection',
    'collectible_model',
    'collectible_backdrop',
    'collectible_symbol',
    'collectible_number',
    'collectible_max_price_stars',
    'collectible_fallback_prize_code',
];

const changePrizeType = (prize, rewardType) => {
    const details = { ...(prize.details || {}) };
    if (rewardType === 'telegram_gift') {
        details.gift_provider = details.gift_provider === 'collectible'
            ? details.gift_provider
            : 'telegram';
    } else {
        GIFT_DETAIL_KEYS.forEach((key) => delete details[key]);
    }
    return {
        reward_type: rewardType,
        reward_value: rewardType === 'none' || rewardType === 'telegram_gift' ? 0 : prize.reward_value,
        details: Object.keys(details).length ? details : null,
    };
};

const resolveAdminAssetUrl = (value) => {
    const path = String(value || '').trim();
    if (!path || /^https?:\/\//i.test(path) || path.startsWith('data:')) return path;
    if (!path.startsWith('/api/')) return path;
    return `${String(API_BASE_URL || '').replace(/\/$/, '')}${path}`;
};

const newPrize = (overrides = {}) => ({
    _key: draftKey('prize'),
    id: null,
    code: '',
    title: '',
    reward_type: 'bonus',
    reward_value: 0,
    source_item_id: null,
    estimated_cost_rub: 0,
    loyalty_eligible: false,
    probability_weight: 1,
    chance_percent: 0,
    stock_total: '',
    stock_used: 0,
    is_active: false,
    details: null,
    spins_count: 0,
    ...overrides,
});

const newTier = (position) => ({
    _key: draftKey('tier'),
    id: null,
    code: '',
    title: '',
    cost_bonus: 25,
    expected_cost_limit_rub: 0,
    daily_limit: 0,
    sort_order: position * 10,
    is_active: false,
    spins_count: 0,
    prizes: [],
    _open: true,
});

const normalizeLoaded = (payload) => ({
    enabled: Boolean(payload?.enabled),
    policy: {
        ...DEFAULT_POLICY,
        ...(payload?.policy || {}),
        loyalty_bands: Array.isArray(payload?.policy?.loyalty_bands) && payload.policy.loyalty_bands.length
            ? payload.policy.loyalty_bands
            : DEFAULT_POLICY.loyalty_bands,
    },
    digital_goods_catalog: Array.isArray(payload?.digital_goods_catalog) ? payload.digital_goods_catalog : [],
    economics: payload?.economics || { monthly_spent_rub: 0, monthly_budget_rub: 0 },
    legal_review: {
        confirmed: Boolean(payload?.legal_review?.confirmed),
        note: payload?.legal_review?.note || '',
        confirmed_at: payload?.legal_review?.confirmed_at || null,
        admin_id: payload?.legal_review?.admin_id ?? null,
    },
    tiers: Array.isArray(payload?.tiers)
        ? payload.tiers.map((tier) => ({
            ...tier,
            _key: `tier-${tier.id}`,
            _open: true,
            prizes: (tier.prizes || []).map((prize) => ({
                ...prize,
                _key: `prize-${prize.id}`,
                stock_total: prize.stock_total ?? '',
            })),
        }))
        : [],
});

const Toggle = ({ checked, onChange, label }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 ${checked ? 'bg-indigo-600' : 'bg-slate-300'}`}
    >
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${checked ? 'left-6' : 'left-1'}`} />
    </button>
);

const Field = ({ label, children, hint }) => (
    <label className="block min-w-0">
        <span className="mb-1.5 block text-xs font-semibold uppercase text-slate-500">{label}</span>
        {children}
        {hint && <span className="mt-1.5 block text-xs leading-5 text-slate-500">{hint}</span>}
    </label>
);

const visualByKey = (value) => PRIZE_VISUALS.find((item) => item.value === value);

const PrizeVisualPicker = ({ value, customImageUrl, prizeTitle, onChange }) => {
    const [open, setOpen] = useState(false);
    const selected = visualByKey(value);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open]);

    const choose = (nextValue) => {
        onChange(nextValue);
        setOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="flex min-h-12 w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 text-left transition hover:border-indigo-300 hover:bg-indigo-50/40 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                aria-label={`Выбрать предустановленную иконку приза ${prizeTitle}`}
            >
                <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg bg-slate-50">
                    {customImageUrl ? (
                        <img src={customImageUrl} alt="" className="h-full w-full object-contain p-1" />
                    ) : selected ? (
                        <img
                            src={`/roulette/telegram-gifts/${selected.value}.webp`}
                            alt=""
                            className="h-9 w-9 object-contain"
                        />
                    ) : <Gift size={18} className="text-slate-400" />}
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-semibold text-slate-800">
                        {customImageUrl ? 'Своя иконка' : selected?.label || 'Автоматически'}
                    </span>
                    <span className="block truncate text-[11px] text-slate-400">
                        {customImageUrl && selected ? `Резерв: ${selected.label}` : 'Открыть каталог'}
                    </span>
                </span>
                <ChevronDown size={15} className="shrink-0 text-slate-400" />
            </button>

            {open && createPortal(
                <div
                    className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setOpen(false);
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Иконка приза ${prizeTitle}`}
                        className="max-h-[88vh] w-full overflow-y-auto rounded-t-lg bg-white shadow-2xl sm:max-w-3xl sm:rounded-lg"
                    >
                        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
                            <div>
                                <h4 className="text-base font-bold text-slate-950">Иконка приза</h4>
                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                    Выберите готовую иллюстрацию. Загруженная вручную иконка останется приоритетной.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                                aria-label="Закрыть каталог иконок"
                            >
                                <X size={17} />
                            </button>
                        </div>

                        <div className="space-y-6 px-5 py-5 sm:px-6">
                            <button
                                type="button"
                                onClick={() => choose('')}
                                className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${!value
                                    ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100'
                                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                            >
                                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-500">
                                    <Gift size={21} />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-semibold text-slate-900">Автоматически</span>
                                    <span className="mt-0.5 block text-xs text-slate-500">Иконка определяется по типу награды</span>
                                </span>
                                {!value && <Check size={18} className="shrink-0 text-indigo-600" />}
                            </button>

                            {PRIZE_VISUAL_GROUPS.map((group) => (
                                <section key={group.id}>
                                    <div className="mb-3">
                                        <h5 className="text-sm font-bold text-slate-900">{group.label}</h5>
                                        <p className="mt-0.5 text-xs text-slate-500">{group.hint}</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-6">
                                        {group.items.map((item) => {
                                            const active = value === item.value;
                                            return (
                                                <button
                                                    key={item.value}
                                                    type="button"
                                                    onClick={() => choose(item.value)}
                                                    className={`relative flex min-h-[132px] flex-col items-center justify-center gap-2 rounded-lg border p-3 text-center transition ${active
                                                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-100'
                                                        : 'border-slate-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:bg-slate-50'}`}
                                                    aria-pressed={active}
                                                >
                                                    {active && (
                                                        <span className="absolute right-2 top-2 grid h-5 w-5 place-items-center rounded-full bg-indigo-600 text-white">
                                                            <Check size={13} />
                                                        </span>
                                                    )}
                                                    <img
                                                        src={`/roulette/telegram-gifts/${item.value}.webp`}
                                                        alt=""
                                                        className="h-20 w-20 object-contain"
                                                        loading="lazy"
                                                    />
                                                    <span className="text-xs font-semibold leading-4 text-slate-700">{item.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
};

const suggestDigitalGoodsVisual = (item) => {
    const haystack = [item?.title, item?.item_id, item?.category_id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    const hasAny = (...tokens) => tokens.some((token) => haystack.includes(token));

    if (hasAny('telegram', 'premium', 'stars')) return 'digital-telegram';
    if (hasAny('spotify', 'netflix', 'youtube', 'music', 'кино', 'ivi', 'okko', 'megogo', 'подпис')) {
        return 'digital-media';
    }
    if (hasAny('apple', 'itunes', 'app store', 'google play')) return 'digital-apps';
    if (hasAny('roblox', 'minecraft', 'pubg', 'free fire', 'mobile legends', 'genshin', 'fortnite', 'riot', 'valorant')) {
        return 'digital-mobile-games';
    }
    if (hasAny('steam', 'playstation', 'play station', 'psn', 'xbox', 'nintendo', 'game', 'игр')) {
        return 'digital-gaming';
    }
    return 'digital-voucher';
};

const isValuablePrize = (prize) => ['stars', 'premium', 'digital_good', 'telegram_gift', 'manual'].includes(prize.reward_type);

const prizeIsEligible = (prize, policy) => (
    prize.is_active
    && asInteger(prize.probability_weight) > 0
    && (prize.stock_total === '' || prize.stock_total === null || asInteger(prize.stock_used) < asInteger(prize.stock_total))
    && (!isValuablePrize(prize) || policy.valuable_prizes_enabled)
    && (prize.reward_type !== 'none' || policy.no_prize_enabled)
);

const prizeChance = (tier, prize, policy) => {
    const available = (tier.prizes || []).filter((item) => (
        prizeIsEligible(item, policy)
    ));
    const total = available.reduce((sum, item) => sum + asInteger(item.probability_weight), 0);
    if (!prize.is_active || total <= 0 || !available.includes(prize)) return 0;
    return (asInteger(prize.probability_weight) * 100) / total;
};

const prizeCost = (prize, policy) => {
    if (prize.reward_type === 'none') return 0;
    if (prize.reward_type === 'bonus') {
        return asInteger(prize.reward_value) * Number(policy.bonus_point_cost_rub || 0);
    }
    return Number(prize.estimated_cost_rub || 0);
};

const expectedTierCost = (tier, policy, useMaxLoyalty = false) => {
    const maxMultiplier = useMaxLoyalty && policy.loyalty_boost_enabled
        ? Math.max(100, ...(policy.loyalty_bands || []).map((item) => asInteger(item.multiplier_percent, 100)))
        : 100;
    const available = (tier.prizes || []).filter((item) => prizeIsEligible(item, policy));
    const rows = available.map((prize) => {
        const multiplier = useMaxLoyalty && prize.loyalty_eligible ? maxMultiplier : 100;
        return {
            cost: prizeCost(prize, policy),
            weight: asInteger(prize.probability_weight) * multiplier,
        };
    });
    const totalWeight = rows.reduce((sum, item) => sum + item.weight, 0);
    if (!totalWeight) return 0;
    return rows.reduce((sum, item) => sum + item.cost * item.weight, 0) / totalWeight;
};

const uniquePrizeCode = (tier, prefix) => {
    const used = new Set((tier.prizes || []).map((item) => String(item.code || '').toLowerCase()));
    const cleanPrefix = String(prefix || 'prize').toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 42) || 'prize';
    let candidate = cleanPrefix;
    let index = 2;
    while (used.has(candidate)) {
        candidate = `${cleanPrefix.slice(0, 46)}-${index}`;
        index += 1;
    }
    return candidate;
};

const RouletteSettingsPanel = ({ onShowToast }) => {
    const [config, setConfig] = useState({
        enabled: false,
        policy: DEFAULT_POLICY,
        digital_goods_catalog: [],
        economics: { monthly_spent_rub: 0, monthly_budget_rub: 0 },
        legal_review: { confirmed: false, note: '', confirmed_at: null, admin_id: null },
        tiers: [],
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPrizeKey, setUploadingPrizeKey] = useState(null);
    const [confirmingLegal, setConfirmingLegal] = useState(false);
    const [legalNote, setLegalNote] = useState('');
    const [error, setError] = useState('');

    const activeSummary = useMemo(() => {
        const activeTiers = config.tiers.filter((tier) => tier.is_active);
        const activePrizes = activeTiers.reduce(
            (total, tier) => total + tier.prizes.filter((prize) => prize.is_active).length,
            0,
        );
        return { tiers: activeTiers.length, prizes: activePrizes };
    }, [config.tiers]);

    const legalConfirmed = Boolean(config.legal_review?.confirmed);

    const load = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось загрузить рулетку');
            const normalized = normalizeLoaded(body);
            setConfig(normalized);
            setLegalNote(normalized.legal_review.note || '');
        } catch (loadError) {
            setError(loadError.message || 'Не удалось загрузить рулетку');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateTier = (tierKey, key, value) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.map((tier) => (
                tier._key === tierKey ? { ...tier, [key]: value } : tier
            )),
        }));
    };

    const updatePrize = (tierKey, prizeKey, key, value) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.map((tier) => (
                tier._key === tierKey
                    ? {
                        ...tier,
                        prizes: tier.prizes.map((prize) => (
                            prize._key === prizeKey ? { ...prize, [key]: value } : prize
                        )),
                    }
                    : tier
            )),
        }));
    };

    const updatePrizeFields = (tierKey, prizeKey, fields) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.map((tier) => (
                tier._key === tierKey
                    ? {
                        ...tier,
                        prizes: tier.prizes.map((prize) => (
                            prize._key === prizeKey ? { ...prize, ...fields } : prize
                        )),
                    }
                    : tier
            )),
        }));
    };

    const uploadPrizeImage = async (tierKey, prize, file) => {
        if (!file) return;
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
            setError('Для иконки разрешены PNG, JPEG и WebP.');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            setError('Размер иконки не должен превышать 2 МБ.');
            return;
        }

        try {
            setUploadingPrizeKey(prize._key);
            setError('');
            const body = new FormData();
            body.append('file', file);
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/media`, {
                method: 'POST',
                credentials: 'include',
                body,
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload.path) {
                throw new Error(payload.detail || 'Не удалось загрузить иконку');
            }
            updatePrize(
                tierKey,
                prize._key,
                'details',
                updatePrizeDetails(prize.details, 'image_url', payload.path),
            );
            onShowToast?.('Иконка загружена. Сохраните настройки рулетки');
        } catch (uploadError) {
            setError(uploadError.message || 'Не удалось загрузить иконку');
        } finally {
            setUploadingPrizeKey(null);
        }
    };

    const updatePolicy = (key, value) => {
        setConfig((current) => ({
            ...current,
            policy: { ...current.policy, [key]: value },
        }));
    };

    const updateLoyaltyBand = (bandIndex, key, value) => {
        setConfig((current) => ({
            ...current,
            policy: {
                ...current.policy,
                loyalty_bands: current.policy.loyalty_bands.map((band, index) => (
                    index === bandIndex ? { ...band, [key]: value } : band
                )),
            },
        }));
    };

    const addTier = () => {
        setConfig((current) => ({
            ...current,
            tiers: [...current.tiers, newTier(current.tiers.length + 1)],
        }));
    };

    const removeDraftTier = (tierKey) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.filter((tier) => tier._key !== tierKey),
        }));
    };

    const addPrize = (tierKey) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.map((tier) => (
                tier._key === tierKey
                    ? { ...tier, _open: true, prizes: [...tier.prizes, newPrize()] }
                    : tier
            )),
        }));
    };

    const addBonusPrize = (tierKey) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.map((tier) => {
                if (tier._key !== tierKey) return tier;
                return {
                    ...tier,
                    _open: true,
                    prizes: [
                        ...tier.prizes,
                        newPrize({
                            code: uniquePrizeCode(tier, 'bonus'),
                            title: 'Бонусный сектор',
                            reward_type: 'bonus',
                            reward_value: 10,
                            probability_weight: 50,
                            is_active: true,
                            details: { visual_key: 'present', rarity_label: 'Бонус' },
                        }),
                    ],
                };
            }),
        }));
    };

    const addEmptyPrize = (tierKey) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.map((tier) => {
                if (tier._key !== tierKey) return tier;
                return {
                    ...tier,
                    _open: true,
                    prizes: [
                        ...tier.prizes,
                        newPrize({
                            code: uniquePrizeCode(tier, 'no-prize'),
                            title: 'Без приза',
                            reward_type: 'none',
                            reward_value: 0,
                            probability_weight: 50,
                            is_active: true,
                            details: { visual_key: 'present', rarity_label: 'Попробуйте ещё' },
                        }),
                    ],
                };
            }),
        }));
    };

    const toggleDigitalGood = (tierKey, item, enabled) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.map((tier) => {
                if (tier._key !== tierKey) return tier;
                const existing = tier.prizes.find((prize) => (
                    prize.reward_type === 'digital_good' && prize.source_item_id === item.item_id
                ));
                if (existing) {
                    return {
                        ...tier,
                        prizes: tier.prizes.map((prize) => (
                            prize._key === existing._key ? { ...prize, is_active: enabled } : prize
                        )),
                    };
                }
                if (!enabled) return tier;
                const itemSuffix = String(item.item_id || '').split(':').pop();
                return {
                    ...tier,
                    prizes: [
                        ...tier.prizes,
                        newPrize({
                            code: uniquePrizeCode(tier, `wata-${itemSuffix}`),
                            title: item.title || 'Цифровой товар WATA',
                            reward_type: 'digital_good',
                            reward_value: 1,
                            source_item_id: item.item_id,
                            estimated_cost_rub: Number(item.source_min_price || 0),
                            loyalty_eligible: true,
                            probability_weight: 1,
                            is_active: true,
                            details: {
                                source: 'wata_digital_goods',
                                source_item_id: item.item_id,
                                visual_key: suggestDigitalGoodsVisual(item),
                                rarity_label: 'Ценный приз',
                            },
                        }),
                    ],
                };
            }),
        }));
    };

    const removeDraftPrize = (tierKey, prizeKey) => {
        setConfig((current) => ({
            ...current,
            tiers: current.tiers.map((tier) => (
                tier._key === tierKey
                    ? { ...tier, prizes: tier.prizes.filter((prize) => prize._key !== prizeKey) }
                    : tier
            )),
        }));
    };

    const handleEnabledChange = (enabled) => {
        if (enabled && !legalConfirmed) {
            setError('Сначала подтвердите юридическую проверку — сервер не включит рулетку без неё.');
            return;
        }
        setConfig((current) => ({ ...current, enabled }));
    };

    const submitLegalReview = async (confirmed) => {
        if (confirmed && legalNote.trim().length < 20) {
            setError('Заметка юридической проверки должна содержать не менее 20 символов.');
            return;
        }
        try {
            setConfirmingLegal(true);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette/legal-review`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirmed, note: legalNote.trim() }),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось сохранить юридическую проверку');
            setConfig((current) => ({
                ...current,
                enabled: confirmed ? current.enabled : false,
                legal_review: body.legal_review || {
                    confirmed,
                    note: confirmed ? legalNote.trim() : '',
                    confirmed_at: null,
                    admin_id: null,
                },
            }));
            if (!confirmed) setLegalNote('');
            if (onShowToast) {
                onShowToast(confirmed ? 'Юридическая проверка подтверждена' : 'Юридическая проверка снята, рулетка выключена');
            }
        } catch (legalError) {
            setError(legalError.message || 'Не удалось сохранить юридическую проверку');
        } finally {
            setConfirmingLegal(false);
        }
    };

    const handleSave = async () => {
        const missingTier = config.tiers.find((tier) => !tier.code.trim() || !tier.title.trim());
        const missingPrize = config.tiers
            .flatMap((tier) => tier.prizes)
            .find((prize) => !prize.code.trim() || !prize.title.trim());
        if (missingTier || missingPrize) {
            setError('Заполните название и системный код у всех уровней и призов.');
            return;
        }
        if (config.enabled && !legalConfirmed) {
            setError('Нельзя включить рулетку без подтверждённой юридической проверки.');
            return;
        }

        const payload = {
            enabled: Boolean(config.enabled),
            policy: {
                version: String(config.policy.version || '1'),
                valuable_prizes_enabled: Boolean(config.policy.valuable_prizes_enabled),
                no_prize_enabled: Boolean(config.policy.no_prize_enabled),
                budget_guard_enabled: Boolean(config.policy.budget_guard_enabled),
                monthly_budget_rub: Number(config.policy.monthly_budget_rub || 0),
                bonus_point_cost_rub: Number(config.policy.bonus_point_cost_rub || 0),
                loyalty_boost_enabled: Boolean(config.policy.loyalty_boost_enabled),
                loyalty_bands: config.policy.loyalty_bands.map((band) => ({
                    code: String(band.code || '').trim().toLowerCase(),
                    title: String(band.title || '').trim(),
                    min_spend_rub: Number(band.min_spend_rub || 0),
                    multiplier_percent: asInteger(band.multiplier_percent, 100),
                })),
            },
            tiers: config.tiers.map((tier) => ({
                ...(isPersisted(tier) ? { id: tier.id } : {}),
                code: tier.code.trim().toLowerCase(),
                title: tier.title.trim(),
                cost_bonus: asInteger(tier.cost_bonus, 0),
                expected_cost_limit_rub: Number(tier.expected_cost_limit_rub || 0),
                daily_limit: asInteger(tier.daily_limit, 0),
                sort_order: asInteger(tier.sort_order, 0),
                is_active: Boolean(tier.is_active),
                prizes: tier.prizes.map((prize) => ({
                    ...(isPersisted(prize) ? { id: prize.id } : {}),
                    code: prize.code.trim().toLowerCase(),
                    title: prize.title.trim(),
                    reward_type: prize.reward_type,
                    reward_value: ['none', 'telegram_gift'].includes(prize.reward_type)
                        ? 0
                        : asInteger(prize.reward_value, 0),
                    source_item_id: prize.source_item_id || null,
                    estimated_cost_rub: Number(prize.estimated_cost_rub || 0),
                    loyalty_eligible: Boolean(prize.loyalty_eligible),
                    probability_weight: asInteger(prize.probability_weight, 0),
                    stock_total: prize.stock_total === '' || prize.stock_total === null
                        ? null
                        : asInteger(prize.stock_total, 0),
                    is_active: Boolean(prize.is_active),
                    details: prize.details || null,
                })),
            })),
        };

        try {
            setSaving(true);
            setError('');
            const response = await fetch(`${API_BASE_URL}/api/settings/roulette`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось сохранить рулетку');
            const normalized = normalizeLoaded(body);
            setConfig(normalized);
            setLegalNote(normalized.legal_review.note || '');
            if (onShowToast) onShowToast('Настройки рулетки сохранены');
        } catch (saveError) {
            setError(saveError.message || 'Не удалось сохранить рулетку');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="py-16 text-center text-slate-500">Загрузка конфигурации рулетки...</div>;
    }

    return (
        <div className="space-y-5">
            {error && (
                <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {error}
                </div>
            )}

            <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex min-w-0 gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                            <Gift size={20} />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Доступность рулетки</h3>
                            <p className="mt-1 text-sm leading-5 text-slate-500">
                                {activeSummary.tiers} активных уровней, {activeSummary.prizes} активных призов. Партнёрские боты исключены.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-4 sm:justify-end">
                        <span className={`text-sm font-semibold ${config.enabled ? 'text-emerald-700' : 'text-slate-500'}`}>
                            {config.enabled ? 'Включена' : 'Выключена'}
                        </span>
                        <Toggle
                            checked={config.enabled}
                            onChange={handleEnabledChange}
                            label="Включить рулетку"
                        />
                    </div>
                </div>
                <div className={`flex gap-3 border-t px-5 py-3 text-sm leading-5 sm:px-6 ${config.enabled ? 'border-emerald-100 bg-emerald-50 text-emerald-800' : 'border-amber-100 bg-amber-50 text-amber-800'}`}>
                    {config.enabled ? <ShieldCheck className="mt-0.5 shrink-0" size={17} /> : <AlertTriangle className="mt-0.5 shrink-0" size={17} />}
                    <span>
                        {config.enabled
                            ? 'После сохранения пользователи смогут тратить бонусы. Сервер не включит рулетку без доступных призов и юридической проверки.'
                            : 'Можно подготовить уровни и вероятности без запуска. Сохранение черновика не откроет рулетку пользователям.'}
                    </span>
                </div>
            </section>

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
                    <div className="flex min-w-0 gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                            <Wallet size={20} />
                        </div>
                        <div>
                            <h3 className="font-semibold text-slate-900">Экономика и режим выдачи</h3>
                            <p className="mt-1 max-w-3xl text-sm leading-5 text-slate-500">
                                Ценные призы, бонусные сектора и исходы без приза управляются отдельно. Сервер применяет правила до выбора результата.
                            </p>
                        </div>
                    </div>
                    <div className="text-sm text-slate-500">
                        Потрачено в этом месяце: <strong className="text-slate-900">{Number(config.economics?.monthly_spent_rub || 0).toLocaleString('ru-RU')} ₽</strong>
                    </div>
                </div>

                <div className="grid divide-y divide-slate-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
                    <div className="flex items-center justify-between gap-4 p-5">
                        <div>
                            <div className="text-sm font-semibold text-slate-900">Ценные призы</div>
                            <div className="mt-1 text-xs leading-5 text-slate-500">WATA, Stars, Premium и ручная выдача</div>
                        </div>
                        <Toggle
                            checked={config.policy.valuable_prizes_enabled}
                            onChange={(value) => updatePolicy('valuable_prizes_enabled', value)}
                            label="Разрешить ценные призы"
                        />
                    </div>
                    <div className="flex items-center justify-between gap-4 p-5">
                        <div>
                            <div className="text-sm font-semibold text-slate-900">Сектор без приза</div>
                            <div className="mt-1 text-xs leading-5 text-slate-500">Показывается пользователю как отдельный исход</div>
                        </div>
                        <Toggle
                            checked={config.policy.no_prize_enabled}
                            onChange={(value) => updatePolicy('no_prize_enabled', value)}
                            label="Разрешить исход без приза"
                        />
                    </div>
                    <div className="flex items-center justify-between gap-4 p-5">
                        <div>
                            <div className="text-sm font-semibold text-slate-900">Контроль бюджета</div>
                            <div className="mt-1 text-xs leading-5 text-slate-500">Не допускает превышения лимитов</div>
                        </div>
                        <Toggle
                            checked={config.policy.budget_guard_enabled}
                            onChange={(value) => updatePolicy('budget_guard_enabled', value)}
                            label="Включить контроль бюджета"
                        />
                    </div>
                </div>

                <div className="grid gap-4 border-t border-slate-100 p-5 sm:grid-cols-2 sm:p-6">
                    <Field label="Месячный бюджет призов, ₽" hint="При исчерпании дорогие исходы исключаются до следующего месяца">
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            className={inputClass}
                            value={config.policy.monthly_budget_rub}
                            onChange={(event) => updatePolicy('monthly_budget_rub', event.target.value)}
                        />
                    </Field>
                    <Field label="Себестоимость 1 бонуса, ₽" hint="Используется только для расчёта экономики бонусных секторов">
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            className={inputClass}
                            value={config.policy.bonus_point_cost_rub}
                            onChange={(event) => updatePolicy('bonus_point_cost_rub', event.target.value)}
                        />
                    </Field>
                </div>

                <div className="border-t border-slate-100 p-5 sm:p-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h4 className="text-sm font-semibold text-slate-900">Поощрение постоянных клиентов</h4>
                            <p className="mt-1 text-xs leading-5 text-slate-500">Коэффициент применяется только к призам с отметкой «Лояльность» и учитывается при проверке бюджета.</p>
                        </div>
                        <Toggle
                            checked={config.policy.loyalty_boost_enabled}
                            onChange={(value) => updatePolicy('loyalty_boost_enabled', value)}
                            label="Включить уровни лояльности"
                        />
                    </div>
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full min-w-[620px] text-left text-sm">
                            <thead className="border-b border-slate-100 text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="pb-2 pr-3 font-semibold">Уровень</th>
                                    <th className="pb-2 pr-3 font-semibold">Покупки от, ₽</th>
                                    <th className="pb-2 font-semibold">Вес отмеченных призов</th>
                                </tr>
                            </thead>
                            <tbody>
                                {config.policy.loyalty_bands.map((band, index) => (
                                    <tr key={`${band.code}-${index}`} className="border-b border-slate-100 last:border-0">
                                        <td className="py-3 pr-3">
                                            <input
                                                className={inputClass}
                                                value={band.title}
                                                onChange={(event) => updateLoyaltyBand(index, 'title', event.target.value)}
                                            />
                                        </td>
                                        <td className="py-3 pr-3">
                                            <input
                                                type="number"
                                                min="0"
                                                step="100"
                                                className={inputClass}
                                                value={band.min_spend_rub}
                                                disabled={index === 0}
                                                onChange={(event) => updateLoyaltyBand(index, 'min_spend_rub', event.target.value)}
                                            />
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="100"
                                                    max="300"
                                                    className={inputClass}
                                                    value={band.multiplier_percent}
                                                    onChange={(event) => updateLoyaltyBand(index, 'multiplier_percent', event.target.value)}
                                                />
                                                <span className="shrink-0 text-sm text-slate-500">%</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                        <ShieldCheck size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900">Юридическая проверка</h3>
                        <p className="mt-1 text-sm leading-5 text-slate-500">
                            Перед публичным включением подтвердите чеклист из docs/LOYALTY_ROULETTE_LEGAL_CHECKLIST.md.
                            Сервер — источник истины: без подтверждения enabled=true отклоняется.
                        </p>
                    </div>
                </div>
                <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold uppercase text-slate-500">Заметка / ссылка на заключение</span>
                    <textarea
                        className="min-h-[96px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                        value={legalNote}
                        onChange={(event) => setLegalNote(event.target.value)}
                        placeholder="Кратко зафиксируйте юрисдикцию, условия, раскрытие вероятностей и подпись counsel (мин. 20 символов)."
                        maxLength={2000}
                    />
                </label>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className={`rounded-lg px-3 py-2 text-sm ${legalConfirmed ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
                        {legalConfirmed
                            ? `Подтверждено${config.legal_review.confirmed_at ? ` · ${new Date(config.legal_review.confirmed_at).toLocaleString('ru-RU')}` : ''}${config.legal_review.admin_id ? ` · admin #${config.legal_review.admin_id}` : ''}`
                            : 'Не подтверждено — включение рулетки заблокировано'}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            disabled={confirmingLegal}
                            onClick={() => submitLegalReview(true)}
                            className="inline-flex h-10 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {confirmingLegal ? 'Сохранение...' : 'Юридическая проверка подтверждена'}
                        </button>
                        {legalConfirmed && (
                            <button
                                type="button"
                                disabled={confirmingLegal}
                                onClick={() => submitLegalReview(false)}
                                className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-rose-300 hover:text-rose-700 disabled:opacity-50"
                            >
                                Снять подтверждение
                            </button>
                        )}
                    </div>
                </div>
            </section>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">Уровни и призы</h3>
                    <p className="mt-1 text-sm text-slate-500">Процент рассчитывается автоматически из весов активных призов.</p>
                </div>
                <button
                    type="button"
                    onClick={addTier}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:text-indigo-700"
                >
                    <PlusCircle size={17} /> Добавить уровень
                </button>
            </div>

            {config.tiers.length === 0 ? (
                <section className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center">
                    <PackageOpen className="mx-auto text-slate-400" size={28} />
                    <h3 className="mt-3 font-semibold text-slate-900">Уровни ещё не созданы</h3>
                    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                        Добавьте первый уровень, затем призы и веса. До отдельного включения всё останется черновиком.
                    </p>
                </section>
            ) : (
                <div className="space-y-4">
                    {config.tiers.map((tier) => (
                        <section key={tier._key} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-100 p-4 sm:p-5">
                                <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
                                    <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(180px,1.2fr)_minmax(150px,0.8fr)_120px_145px_120px_90px]">
                                        <Field label="Название уровня">
                                            <input
                                                className={inputClass}
                                                value={tier.title}
                                                placeholder="Например, Стартовая"
                                                onChange={(event) => updateTier(tier._key, 'title', event.target.value)}
                                            />
                                        </Field>
                                        <Field label="Системный код" hint={isPersisted(tier) ? 'После сохранения не меняется' : 'Латиница, цифры, - и _'}>
                                            <input
                                                className={inputClass}
                                                value={tier.code}
                                                disabled={isPersisted(tier)}
                                                placeholder="starter"
                                                onChange={(event) => updateTier(tier._key, 'code', event.target.value)}
                                            />
                                        </Field>
                                        <Field label="Цена, бонусов">
                                            <input
                                                type="number"
                                                min="1"
                                                className={inputClass}
                                                value={tier.cost_bonus}
                                                onChange={(event) => updateTier(tier._key, 'cost_bonus', event.target.value)}
                                            />
                                        </Field>
                                        <Field label="Лимит затрат, ₽" hint="На одно вращение">
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className={inputClass}
                                                value={tier.expected_cost_limit_rub}
                                                onChange={(event) => updateTier(tier._key, 'expected_cost_limit_rub', event.target.value)}
                                            />
                                        </Field>
                                        <Field label="Лимит в день" hint="0 — без лимита">
                                            <input
                                                type="number"
                                                min="0"
                                                className={inputClass}
                                                value={tier.daily_limit}
                                                onChange={(event) => updateTier(tier._key, 'daily_limit', event.target.value)}
                                            />
                                        </Field>
                                        <Field label="Порядок">
                                            <input
                                                type="number"
                                                className={inputClass}
                                                value={tier.sort_order}
                                                onChange={(event) => updateTier(tier._key, 'sort_order', event.target.value)}
                                            />
                                        </Field>
                                    </div>
                                    <div className="flex h-10 shrink-0 items-center gap-3">
                                        <span className="text-xs font-semibold text-slate-500">{tier.is_active ? 'Активен' : 'Скрыт'}</span>
                                        <Toggle
                                            checked={tier.is_active}
                                            onChange={(value) => updateTier(tier._key, 'is_active', value)}
                                            label={`Активировать уровень ${tier.title || tier.code}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => updateTier(tier._key, '_open', !tier._open)}
                                            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                            title={tier._open ? 'Свернуть' : 'Развернуть'}
                                        >
                                            {tier._open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                        </button>
                                        {!isPersisted(tier) && (
                                            <button
                                                type="button"
                                                onClick={() => removeDraftTier(tier._key)}
                                                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                                                title="Удалить несохранённый уровень"
                                            >
                                                <Trash2 size={17} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {isPersisted(tier) && (
                                    <div className="mt-3 text-xs text-slate-500">
                                        Вращений: {asInteger(tier.spins_count).toLocaleString('ru-RU')} · ожидаемая себестоимость сейчас {Number(tier.expected_cost_rub || expectedTierCost(tier, config.policy)).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽ · при максимальной лояльности {Number(tier.max_loyalty_expected_cost_rub || expectedTierCost(tier, config.policy, true)).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽.
                                    </div>
                                )}
                            </div>

                            {tier._open && (
                                <div>
                                    <div className="flex items-center justify-between gap-4 bg-slate-50 px-4 py-3 sm:px-5">
                                        <div className="text-sm font-semibold text-slate-700">Призы · {tier.prizes.length}</div>
                                        <div className="flex flex-wrap justify-end gap-2">
                                            <button
                                                type="button"
                                                onClick={() => addBonusPrize(tier._key)}
                                                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700"
                                            >
                                                <Coins size={16} /> Бонусный сектор
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => addEmptyPrize(tier._key)}
                                                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700"
                                            >
                                                <PackageOpen size={16} /> Без приза
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => addPrize(tier._key)}
                                                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-indigo-300 hover:text-indigo-700"
                                            >
                                                <PlusCircle size={16} /> Другой приз
                                            </button>
                                        </div>
                                    </div>

                                    <div className="border-b border-slate-100 px-4 py-4 sm:px-5">
                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                                            <div>
                                                <h4 className="text-sm font-semibold text-slate-900">Пул цифровых товаров WATA</h4>
                                                <p className="mt-1 text-xs leading-5 text-slate-500">Отметьте товары, которые участвуют в этом уровне. Себестоимость берётся из раздела «Цифровые товары» и проверяется сервером.</p>
                                            </div>
                                            <span className="text-xs text-slate-500">Выбрано: {tier.prizes.filter((prize) => prize.reward_type === 'digital_good' && prize.is_active).length}</span>
                                        </div>
                                        {config.digital_goods_catalog.length ? (
                                            <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                                                {config.digital_goods_catalog.map((item) => {
                                                    const selected = tier.prizes.find((prize) => (
                                                        prize.reward_type === 'digital_good' && prize.source_item_id === item.item_id
                                                    ));
                                                    const checked = Boolean(selected?.is_active);
                                                    return (
                                                        <label key={item.item_id} className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 transition ${checked ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                                                            <input
                                                                type="checkbox"
                                                                className="mt-1 h-4 w-4 accent-indigo-600"
                                                                checked={checked}
                                                                onChange={(event) => toggleDigitalGood(tier._key, item, event.target.checked)}
                                                            />
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block truncate text-sm font-semibold text-slate-900">{item.title}</span>
                                                                <span className="mt-1 block text-xs text-slate-500">
                                                                    {item.source_min_price == null ? 'Себестоимость не синхронизирована' : `${Number(item.source_min_price).toLocaleString('ru-RU')} ₽`}
                                                                    {selected ? ` · вес ${asInteger(selected.probability_weight)}` : ''}
                                                                </span>
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="mt-3 rounded-lg border border-dashed border-slate-300 px-4 py-4 text-sm text-slate-500">
                                                В разделе «Цифровые товары WATA» пока нет товаров с галочкой «В боте».
                                            </div>
                                        )}
                                    </div>

                                    {tier.prizes.length === 0 ? (
                                        <div className="px-5 py-8 text-center text-sm text-slate-500">У этого уровня пока нет призов.</div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full min-w-[1720px] border-collapse text-left">
                                                <thead>
                                                    <tr className="border-y border-slate-100 bg-white text-xs uppercase text-slate-500">
                                                        <th className="px-4 py-3 font-semibold sm:pl-5">Приз</th>
                                                        <th className="px-4 py-3 font-semibold">Изображение</th>
                                                        <th className="px-4 py-3 font-semibold">Тип</th>
                                                        <th className="px-4 py-3 font-semibold">Размер</th>
                                                        <th className="px-4 py-3 font-semibold">Себестоимость</th>
                                                        <th className="px-4 py-3 font-semibold">Вес</th>
                                                        <th className="px-4 py-3 font-semibold">Шанс</th>
                                                        <th className="px-4 py-3 font-semibold">Лояльность</th>
                                                        <th className="px-4 py-3 font-semibold">Запас</th>
                                                        <th className="px-4 py-3 font-semibold">Статус</th>
                                                        <th className="px-4 py-3 font-semibold"><span className="sr-only">Действия</span></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tier.prizes.map((prize) => {
                                                        const chance = prizeChance(tier, prize, config.policy);
                                                        const reward = REWARD_TYPES.find((item) => item.value === prize.reward_type);
                                                        return (
                                                            <React.Fragment key={prize._key}>
                                                            <tr className="border-b border-slate-100 align-top last:border-b-0">
                                                                <td className="w-[320px] px-4 py-4 sm:pl-5">
                                                                    <input
                                                                        className={inputClass}
                                                                        value={prize.title}
                                                                        placeholder="Название приза"
                                                                        onChange={(event) => updatePrize(tier._key, prize._key, 'title', event.target.value)}
                                                                    />
                                                                    <input
                                                                        className={`${inputClass} mt-2 font-mono text-xs`}
                                                                        value={prize.code}
                                                                        disabled={isPersisted(prize)}
                                                                        placeholder="prize-code"
                                                                        onChange={(event) => updatePrize(tier._key, prize._key, 'code', event.target.value)}
                                                                    />
                                                                    {isPersisted(prize) && (
                                                                        <div className="mt-2 text-xs text-slate-400">Выигрышей: {asInteger(prize.spins_count)}</div>
                                                                    )}
                                                                </td>
                                                                <td className="w-[240px] px-4 py-4">
                                                                    <PrizeVisualPicker
                                                                        value={prize.details?.visual_key || ''}
                                                                        customImageUrl={resolveAdminAssetUrl(prize.details?.image_url)}
                                                                        prizeTitle={prize.title || prize.code || 'без названия'}
                                                                        onChange={(visualKey) => updatePrize(
                                                                            tier._key,
                                                                            prize._key,
                                                                            'details',
                                                                            updatePrizeDetails(prize.details, 'visual_key', visualKey),
                                                                        )}
                                                                    />
                                                                    <div className="mt-2 flex items-center gap-2">
                                                                        <label
                                                                            className={`inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 text-xs font-semibold text-slate-600 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 ${uploadingPrizeKey === prize._key ? 'pointer-events-none opacity-60' : ''}`}
                                                                            title="Загрузить свою иконку приза"
                                                                        >
                                                                            <Upload size={14} />
                                                                            {uploadingPrizeKey === prize._key
                                                                                ? 'Загрузка...'
                                                                                : prize.details?.image_url ? 'Заменить' : 'Своя иконка'}
                                                                            <input
                                                                                type="file"
                                                                                accept="image/png,image/jpeg,image/webp"
                                                                                className="sr-only"
                                                                                disabled={uploadingPrizeKey === prize._key}
                                                                                onChange={(event) => {
                                                                                    uploadPrizeImage(tier._key, prize, event.target.files?.[0]);
                                                                                    event.target.value = '';
                                                                                }}
                                                                            />
                                                                        </label>
                                                                        {prize.details?.image_url && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => updatePrize(
                                                                                    tier._key,
                                                                                    prize._key,
                                                                                    'details',
                                                                                    updatePrizeDetails(prize.details, 'image_url', ''),
                                                                                )}
                                                                                className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-400 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                                                                                title="Убрать свою иконку"
                                                                                aria-label={`Убрать свою иконку приза ${prize.title || prize.code}`}
                                                                            >
                                                                                <X size={15} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    <p className="mt-1.5 text-[11px] leading-4 text-slate-400">
                                                                        Своя иконка имеет приоритет. PNG, JPEG или WebP до 2 МБ.
                                                                    </p>
                                                                    <input
                                                                        className={`${inputClass} mt-2`}
                                                                        maxLength={32}
                                                                        value={prize.details?.rarity_label || ''}
                                                                        placeholder="Редкость (необязательно)"
                                                                        aria-label={`Подпись редкости приза ${prize.title || prize.code}`}
                                                                        onChange={(event) => updatePrize(
                                                                            tier._key,
                                                                            prize._key,
                                                                            'details',
                                                                            updatePrizeDetails(prize.details, 'rarity_label', event.target.value),
                                                                        )}
                                                                    />
                                                                </td>
                                                                <td className="w-[200px] px-4 py-4">
                                                                    <select
                                                                        className={inputClass}
                                                                        value={prize.reward_type}
                                                                        disabled={prize.reward_type === 'digital_good'}
                                                                        onChange={(event) => updatePrizeFields(
                                                                            tier._key,
                                                                            prize._key,
                                                                            changePrizeType(prize, event.target.value),
                                                                        )}
                                                                    >
                                                                        {REWARD_TYPES.map((item) => (
                                                                            <option
                                                                                key={item.value}
                                                                                value={item.value}
                                                                                disabled={item.value === 'digital_good' && prize.reward_type !== 'digital_good'}
                                                                            >
                                                                                {item.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <div className="mt-2 text-xs leading-4 text-slate-400">{reward?.hint}</div>
                                                                </td>
                                                                <td className="w-[130px] px-4 py-4">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        className={inputClass}
                                                                        value={['none', 'telegram_gift'].includes(prize.reward_type) ? 0 : prize.reward_value}
                                                                        disabled={['none', 'telegram_gift'].includes(prize.reward_type)}
                                                                        onChange={(event) => updatePrize(tier._key, prize._key, 'reward_value', event.target.value)}
                                                                    />
                                                                </td>
                                                                <td className="w-[150px] px-4 py-4">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        className={inputClass}
                                                                        value={prize.reward_type === 'bonus'
                                                                            ? prizeCost(prize, config.policy)
                                                                            : prize.estimated_cost_rub}
                                                                        disabled={prize.reward_type === 'none' || prize.reward_type === 'bonus' || prize.reward_type === 'digital_good'}
                                                                        onChange={(event) => updatePrize(tier._key, prize._key, 'estimated_cost_rub', event.target.value)}
                                                                    />
                                                                    {prize.reward_type === 'digital_good' && (
                                                                        <div className="mt-2 max-w-[150px] truncate text-xs text-slate-400" title={prize.source_item_id || ''}>Из WATA</div>
                                                                    )}
                                                                </td>
                                                                <td className="w-[110px] px-4 py-4">
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        className={inputClass}
                                                                        value={prize.probability_weight}
                                                                        onChange={(event) => updatePrize(tier._key, prize._key, 'probability_weight', event.target.value)}
                                                                    />
                                                                </td>
                                                                <td className="w-[100px] px-4 py-4">
                                                                    <div className="flex h-10 items-center font-semibold text-slate-800">
                                                                        {chance.toLocaleString('ru-RU', { maximumFractionDigits: 3 })}%
                                                                    </div>
                                                                </td>
                                                                <td className="w-[130px] px-4 py-4">
                                                                    <div className="flex h-10 items-center gap-2">
                                                                        <Toggle
                                                                            checked={Boolean(prize.loyalty_eligible)}
                                                                            onChange={(value) => updatePrize(tier._key, prize._key, 'loyalty_eligible', value)}
                                                                            label={`Повышать вес приза ${prize.title || prize.code} для постоянных клиентов`}
                                                                        />
                                                                        <span className="text-xs font-semibold text-slate-500">{prize.loyalty_eligible ? 'Да' : 'Нет'}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="w-[150px] px-4 py-4">
                                                                    <input
                                                                        type="number"
                                                                        min={asInteger(prize.stock_used)}
                                                                        className={inputClass}
                                                                        value={prize.stock_total}
                                                                        placeholder="Без лимита"
                                                                        onChange={(event) => updatePrize(tier._key, prize._key, 'stock_total', event.target.value)}
                                                                    />
                                                                    <div className="mt-2 text-xs text-slate-400">Разыграно: {asInteger(prize.stock_used)}</div>
                                                                </td>
                                                                <td className="w-[130px] px-4 py-4">
                                                                    <div className="flex h-10 items-center gap-2">
                                                                        <Toggle
                                                                            checked={prize.is_active}
                                                                            onChange={(value) => updatePrize(tier._key, prize._key, 'is_active', value)}
                                                                            label={`Активировать приз ${prize.title || prize.code}`}
                                                                        />
                                                                        <span className="text-xs font-semibold text-slate-500">
                                                                            {prize.is_active ? 'Активен' : 'Скрыт'}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td className="w-[64px] px-4 py-4">
                                                                    {!isPersisted(prize) ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeDraftPrize(tier._key, prize._key)}
                                                                            className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                                                                            title="Удалить несохранённый приз"
                                                                        >
                                                                            <Trash2 size={17} />
                                                                        </button>
                                                                    ) : (
                                                                        <div className="flex h-10 w-10 items-center justify-center text-slate-300" title="Сохранённый приз можно только скрыть">
                                                                            <Archive size={17} />
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                            {prize.reward_type === 'telegram_gift' && (
                                                                <tr className="border-b border-indigo-100 bg-indigo-50/40">
                                                                    <td colSpan="11" className="px-4 py-4 sm:px-5">
                                                                        <div className="flex flex-col gap-4">
                                                                            <div>
                                                                                <h5 className="text-sm font-semibold text-slate-900">Автоматическая выдача подарка</h5>
                                                                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                                                                    Выберите источник и задайте жёсткий лимит цены. Общие аккаунты и режим без списаний настраиваются во вкладке «Автовыдача».
                                                                                </p>
                                                                            </div>
                                                                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                                                                <Field label="Источник">
                                                                                    <select
                                                                                        className={inputClass}
                                                                                        value={prize.details?.gift_provider || 'telegram'}
                                                                                        onChange={(event) => updatePrize(
                                                                                            tier._key,
                                                                                            prize._key,
                                                                                            'details',
                                                                                            updatePrizeDetails(prize.details, 'gift_provider', event.target.value),
                                                                                        )}
                                                                                    >
                                                                                        <option value="telegram">Telegram Bot API</option>
                                                                                        <option value="collectible">Collectible Marketplace</option>
                                                                                    </select>
                                                                                </Field>

                                                                                {(prize.details?.gift_provider || 'telegram') === 'telegram' && (
                                                                                    <>
                                                                                        <Field label="Telegram gift_id" hint="ID из getAvailableGifts">
                                                                                            <input
                                                                                                className={inputClass}
                                                                                                value={prize.details?.telegram_gift_id || ''}
                                                                                                placeholder="5170145012310081615"
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'telegram_gift_id', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                        <Field label="Максимальная цена, Stars" hint="Выдача дороже лимита блокируется">
                                                                                            <input
                                                                                                type="number"
                                                                                                min="1"
                                                                                                className={inputClass}
                                                                                                value={prize.details?.telegram_max_price_stars || ''}
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'telegram_max_price_stars', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                    </>
                                                                                )}
                                                                                {prize.details?.gift_provider === 'collectible' && (
                                                                                    <>
                                                                                        <Field label="Коллекция" hint="Обязательно для Marketplace">
                                                                                            <input
                                                                                                className={inputClass}
                                                                                                value={prize.details?.collectible_collection || ''}
                                                                                                placeholder="Loot Bag"
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'collectible_collection', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                        <Field label="Модель" hint="Необязательный фильтр">
                                                                                            <input
                                                                                                className={inputClass}
                                                                                                value={prize.details?.collectible_model || ''}
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'collectible_model', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                        <Field label="Макс. цена, Stars">
                                                                                            <input
                                                                                                type="number"
                                                                                                min="1"
                                                                                                className={inputClass}
                                                                                                value={prize.details?.collectible_max_price_stars || ''}
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'collectible_max_price_stars', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                        <Field label="Номер" hint="Необязательно">
                                                                                            <input
                                                                                                className={inputClass}
                                                                                                value={prize.details?.collectible_number || ''}
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'collectible_number', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                        <Field label="Фон">
                                                                                            <input
                                                                                                className={inputClass}
                                                                                                value={prize.details?.collectible_backdrop || ''}
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'collectible_backdrop', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                        <Field label="Символ">
                                                                                            <input
                                                                                                className={inputClass}
                                                                                                value={prize.details?.collectible_symbol || ''}
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'collectible_symbol', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                        <Field label="Fallback prize code" hint="Если нет лота в лимите">
                                                                                            <input
                                                                                                className={inputClass}
                                                                                                value={prize.details?.collectible_fallback_prize_code || ''}
                                                                                                onChange={(event) => updatePrize(
                                                                                                    tier._key,
                                                                                                    prize._key,
                                                                                                    'details',
                                                                                                    updatePrizeDetails(prize.details, 'collectible_fallback_prize_code', event.target.value),
                                                                                                )}
                                                                                            />
                                                                                        </Field>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    ))}
                </div>
            )}

            <div className="flex justify-end pt-1">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <Save size={18} /> {saving ? 'Сохранение...' : 'Сохранить рулетку'}
                </button>
            </div>
        </div>
    );
};

export default RouletteSettingsPanel;
